'use client'

import { ChevronRightIcon } from '@repo/ui/ChevronRightIcon'
import { Link, useLocation } from 'react-router-dom'
import { useState } from 'react'
import { useGlobalSettingsStore } from '@repo/store/useGlobalSettingsStore'
import { useMenuItemsStore } from '@repo/store/useMenuItemsStore'
import { useTheme } from '@mui/material/styles'
import { useUrlStore } from '@repo/store/useUrlStore'
import MenuItem from '@mui/material/MenuItem'
import MUIStack from '@mui/material/Stack'
import MUITab from '@mui/material/Tab'
import MUITabs from '@mui/material/Tabs'
import Popover from '@mui/material/Popover'
import styles from './ClientWorkspaceNavigation.module.scss'

const clientWorkspaceLinksArray = [
  {
    link: '/case-management/clients/client-workspace',
    title: 'Dashboard',
    dropdown: false
  },
  {
    link: '/case-management/clients/custom-data-pages',
    title: 'Custom data pages',
    dropdown: false
  },
  {
    link: '/case-management/clients/matter-history',
    title: 'Global docs',
    dropdown: false
  },
  {
    link: '/case-management/clients/client-portal/enable-client-portal',
    title: 'Enable client access'
  },
  {
    link: '/case-management/clients/client-portal/enable-associate-portal',
    title: 'Enable associate access'
  }
]

export const ClientWorkspaceNavigation = () => {
  const { pageTitleBlockIsVisible } = useGlobalSettingsStore()
  const { allowedMenuIds } = useMenuItemsStore()
  const { setPostLoadAction, iframeSrc } = useUrlStore()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [flip, setFlip] = useState<boolean>(false)

  const theme = useTheme()

  const useRouteMatch = (patterns: any) => {
    const location = useLocation()
    return patterns.find((pattern: any) => location.pathname.startsWith(pattern)) || null
  }

  const routeMatch = useRouteMatch(clientWorkspaceLinksArray.map(({ link }) => link))

  const setGlobalDocsPostLoadAction = () => {
    const postLoadAction = 'globalDocs'
    const postLoadPage = 'matterhistory.aspx'

    // find the DOM element where dev td has content 'Global:' so we can click it
    const findPostLoadSelector = () => {
      const iframe: HTMLIFrameElement | null = document.querySelector('iframe[name="ospreyIframe"]')
      const iframeDocument = iframe?.contentWindow?.document

      // this is horrible - to find a reference to the global checkbox we need to find the id of the div that contains the text 'Global:'
      const elements: Array<HTMLElement> | undefined = iframeDocument
        ? Array.from(iframeDocument.querySelectorAll('td div'))
        : []
      const element: HTMLElement | null | undefined =
        elements && elements.length ? elements?.find((el: HTMLElement) => el.textContent === 'Global:') : null
      const elementId: string | null | undefined = element?.getAttribute('id')

      // then we can find the checkbox input that is a sibling of the div
      const postLoadSelector = elementId ? `#${elementId} input[type="checkbox"]` : null
      return postLoadSelector
    }

    const postLoadActionShouldWaitForIframeToLoad = !iframeSrc.toLowerCase().includes(postLoadPage)

    setPostLoadAction({
      postLoadAction,
      postLoadActionShouldWaitForIframeToLoad,
      postLoadPage,
      postLoadSelector: findPostLoadSelector
    })
  }

  const currentTab = routeMatch ?? ''

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
    setFlip(true)
  }

  const handlePopoverClose = () => {
    setAnchorEl(null)
    setFlip(false)
  }

  const handleTabChange = (link: string) => {
    setAnchorEl(null)
    setFlip(false)
  }

  const open = Boolean(anchorEl)

  return (
    <MUIStack
      flexDirection={'row'}
      justifyContent={'flex-start'}
      alignItems={'center'}
      gap={'3rem'}
      whiteSpace={'pre'}
      className={styles.navContainer}
      sx={{ marginTop: pageTitleBlockIsVisible ? 0 : '140px' }}
      data-testid="ClientWorkspaceNavigation"
    >
      <MUITabs
        value={currentTab || false}
        aria-label="ClientWorkspaceNavigation"
        className="workspace-details-tabs"
        sx={{ borderBottom: 'none', mt: '0px' }}
      >
        <MUITab
          label="Dashboard"
          role="tab"
          className={`workspace-details-tab ${allowedMenuIds?.includes(1100) ? '' : styles.disabled}`}
          value="/case-management/clients/client-workspace"
          to="/case-management/clients/client-workspace"
          component={Link}
          sx={{
            pointerEvents: allowedMenuIds?.includes(1100) ? 'auto' : 'none',
            opacity: allowedMenuIds?.includes(1100) ? 1 : 0.5
          }}
        />
        <MUITab
          label="Custom data pages"
          role="tab"
          className={`workspace-details-tab ${allowedMenuIds?.includes(3200) ? '' : styles.disabled}`}
          value="/case-management/clients/custom-data-pages"
          component={Link}
          to="/case-management/clients/custom-data-pages?doss=client"
          sx={{
            pointerEvents: allowedMenuIds?.includes(3200) ? 'auto' : 'none',
            opacity: allowedMenuIds?.includes(3200) ? 1 : 0.5
          }}
        />
        <MUITab
          label="Global docs"
          role="tab"
          className={`workspace-details-tab ${allowedMenuIds?.includes(3990) ? '' : styles.disabled}`}
          value="/case-management/clients/matter-history"
          to="/case-management/clients/matter-history?global=true"
          onClick={setGlobalDocsPostLoadAction}
          component={Link}
          sx={{
            pointerEvents: allowedMenuIds?.includes(3990) ? 'auto' : 'none',
            opacity: allowedMenuIds?.includes(3990) ? 1 : 0.5
          }}
        />
        <MUITab
          aria-label="custom-data-popover"
          label="Client portal"
          role="tab"
          className={`workspace-details-tabs ${allowedMenuIds?.some((id: number) => [1131, 1134].includes(id)) ? '' : styles.disabled}`}
          value={
            currentTab === '/case-management/clients/client-portal/enable-client-portal'
              ? '/case-management/clients/client-portal/enable-client-portal'
              : '/case-management/clients/client-portal/enable-associate-portal'
          }
          onClick={handlePopoverOpen}
          aria-controls="custom-data-popover"
          aria-haspopup="true"
          sx={{
            pointerEvents: allowedMenuIds?.some((id: number) => [1131, 1134].includes(id)) ? 'auto' : 'none',
            opacity: allowedMenuIds?.some((id: number) => [1131, 1134].includes(id)) ? 1 : 0.5
          }}
        />
        <ChevronRightIcon
          className={`${styles.navChevron} ${flip ? styles.flip : ''}`}
          onClick={handlePopoverOpen}
          sx={{
            color: theme.palette.text.secondary
          }}
        />
      </MUITabs>

      <Popover
        id="custom-data-popover"
        open={open}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center'
        }}
        slotProps={{ paper: { sx: { overflow: 'visible', backgroundColor: '#FFF' } } }}
      >
        <div className={styles.popoverArrow}></div>
        <div style={{ padding: '6px' }}>
          <MenuItem
            className={styles.popoverItem}
            onClick={() => handleTabChange('/case-management/clients/client-portal/enable-client-portal')}
            component={Link}
            to="/case-management/clients/client-portal/enable-client-portal"
            sx={{
              pointerEvents: allowedMenuIds?.some((id: number) => [1131].includes(id)) ? 'auto' : 'none',
              opacity: allowedMenuIds?.some((id: number) => [1131].includes(id)) ? 1 : 0.5
            }}
          >
            Enable client access
          </MenuItem>
          <MenuItem
            className={styles.popoverItem}
            onClick={() => handleTabChange('/case-management/clients/client-portal/enable-associate-portal')}
            component={Link}
            to="/case-management/clients/client-portal/enable-associate-portal"
            sx={{
              pointerEvents: allowedMenuIds?.some((id: number) => [1134].includes(id)) ? 'auto' : 'none',
              opacity: allowedMenuIds?.some((id: number) => [1134].includes(id)) ? 1 : 0.5
            }}
          >
            Enable associate access
          </MenuItem>
        </div>
      </Popover>
    </MUIStack>
  )
}
